<template>
  <nav v-show="displayOn" class="backdrop-blur bg-white/75">
    <slot></slot>
    <div class="max-w-10xl mx-auto border-b-2 border-b-brand-800">
      <div class="flex h-14">
        <div class="flex z-10 cursor-pointer w-full" @click="goToRoot">
          <img
            v-if="$store.getters.merchant"
            class="w-14"
            :src="$store.getters.merchant?.attributes.avatar_url"
            :alt="$store.getters.imageAlt($store.getters.merchant?.attributes.user_name)"
          />
          <div
            v-if="!emptyMiddle && $store.getters.merchant?.attributes.user_name"
            class="flex items-center h-full w-full sm:w-fit"
          >
            <div class="text-black font-semibold sm:text-black z-10 sm:px-4 px-3 text-lg sm:text-xl tracking-tighter">
              {{ $store.getters.merchant?.attributes.user_name }}
            </div>
          </div>
        </div>

        <div
          v-if="showV2Badge"
          class="flex items-center mr-3"
          data-testid="v2-override-badge"
          title="Rendering v2 via ?version_2=true URL override"
        >
          <span class="text-xs font-semibold px-2 py-1 bg-brand-800 text-white rounded tracking-wider">V2</span>
        </div>

        <MerchantCartPill
          v-if="cartModals && showCartPill && cartQuantity > 0"
          :quantity="cartQuantity"
          :total="cartTotal"
          :show-price="showPrice"
          class="self-center flex-none mr-3"
          @click="$emit('showCart', true)"
        />

        <MerchantNavMenu :empty-middle="emptyMiddle" button-id="main-menu-button" />
      </div>
    </div>
    <ImpersonationBanner />
  </nav>
</template>

<script>
import { ref, onBeforeUnmount, computed } from 'vue'
import { useStore } from 'vuex'
import MerchantNavMenu from '@/components/Merchant/MerchantNavMenu'
import MerchantCartPill from '@/components/Merchant/MerchantCartPill.vue'
import ImpersonationBanner from '@/components/Reseller/ImpersonationBanner'
import { cartPillSummary } from '@/utils/cartPillSummary'
import { isV2Override } from '@/router'

export default {
  name: 'MerchantNav',
  components: { MerchantNavMenu, MerchantCartPill, ImpersonationBanner },

  props: {
    disableHideOnScroll: { type: Boolean, default: false },
    emptyMiddle: { type: Boolean, default: false },
    // v2 sources categories from TanStack Query (Vuex `categories` is empty on
    // v2), so callers pass the count explicitly. When null, fall back to the
    // Vuex getter — keeps v1 behavior unchanged.
    categoryCount: { type: Number, default: null },
    // The nav is on almost every view; only the two selection views carry a cart worth summarising.
    showCartPill: { type: Boolean, default: false },
    // Same arrangement as categoryCount: V2's cart lives in TanStack Query rather than Vuex, so it
    // passes these in and V1 falls through to the getters. One component, one rendering - which is
    // what keeps the pill from drifting between the two checkouts.
    v2CartQuantity: { type: Number, default: null },
    v2CartTotal: { type: String, default: null },
    v2ShowPrices: { type: Boolean, default: null }
  },

  emits: ['showCart'],

  setup(props) {
    const shouldShowHeader = ref(true)
    const store = useStore()
    const categories = computed(() => store.getters.categories)

    const listener = () => {
      const count = props.categoryCount != null ? props.categoryCount : categories.value.length
      shouldShowHeader.value = window.scrollY <= 30 || count <= 1
    }

    document.addEventListener('scroll', listener)

    onBeforeUnmount(() => {
      document.removeEventListener('scroll', listener)
    })

    return {
      shouldShowHeader
    }
  },

  computed: {
    displayOn() {
      return this.shouldShowHeader || this.disableHideOnScroll
    },
    showV2Badge() {
      return isV2Override()
    },

    cartModals() {
      return this.$store.getters.hasCartModals
    },

    cartPill() {
      return cartPillSummary(this.$store, {
        quantity: this.v2CartQuantity,
        total: this.v2CartTotal,
        showPrices: this.v2ShowPrices
      })
    },

    cartQuantity() {
      return this.cartPill.quantity
    },

    cartTotal() {
      return this.cartPill.total
    },

    showPrice() {
      return this.cartPill.showPrice
    }
  },

  methods: {
    goToRoot() {
      let avatarClickUrl = this.$store.getters.merchant?.attributes.avatar_click_url

      if (avatarClickUrl) {
        if (!avatarClickUrl.startsWith('http://') && !avatarClickUrl.startsWith('https://')) {
          avatarClickUrl = 'https://' + avatarClickUrl
        }

        window.open(avatarClickUrl, '_blank')
      } else {
        this.$router.push({ name: 'Home' })
      }
    }
  }
}
</script>
